import { useState, useEffect, useRef } from 'react'
import { Package as PackageIcon } from 'lucide-react'
import { useImageCache } from '../hooks/useImageCache'

interface ProductImageProps {
  imageKey?: string | null
  className?: string
  alt?: string
}

const ProductImage = ({ 
  imageKey, 
  className = '', 
  alt = 'Product image'
}: ProductImageProps) => {
  const [signedUrl, setSignedUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const currentKeyRef = useRef<string | null>(null)
  const { getCachedUrl, loadImageUrl } = useImageCache()
  const getCachedUrlRef = useRef(getCachedUrl)
  const loadImageUrlRef = useRef(loadImageUrl)

  // Mantener referencias estables
  useEffect(() => {
    getCachedUrlRef.current = getCachedUrl
    loadImageUrlRef.current = loadImageUrl
  }, [getCachedUrl, loadImageUrl])

  const loadingRef = useRef(false)

  useEffect(() => {
    // Resetear estado si cambia el imageKey
    if (currentKeyRef.current !== imageKey) {
      setSignedUrl(null)
      setError(false)
      currentKeyRef.current = imageKey || null
      loadingRef.current = false
    }

    // Prevenir ejecuciones duplicadas causadas por React StrictMode
    if (loadingRef.current) return

    if (imageKey && imageKey.trim() !== '') {
      loadingRef.current = true
      loadSignedUrl(imageKey).finally(() => {
        loadingRef.current = false
      })
    } else {
      setSignedUrl(null)
      setError(false)
      setLoading(false)
    }
  }, [imageKey])

  const loadSignedUrl = async (key: string) => {
    try {
      setLoading(true)
      setError(false)
      
      // Validar que la key no esté vacía
      if (!key || key.trim() === '') {
        setError(true)
        setLoading(false)
        return
      }

      // Primero verificar el caché
      const cachedUrl = getCachedUrlRef.current(key)
      if (cachedUrl) {
        setSignedUrl(cachedUrl)
        setLoading(false)
        return
      }

      // Si no está en caché, cargarla
      const url = await loadImageUrlRef.current(key)
      if (url) {
        setSignedUrl(url)
      } else {
        setError(true)
        setSignedUrl(null)
      }
    } catch (error: any) {
      console.error(`Error loading product image signed URL for key "${imageKey}":`, error)
      setError(true)
      setSignedUrl(null)
    } finally {
      setLoading(false)
    }
  }

  // Si no hay imageKey o hay error, mostrar placeholder
  if (!imageKey || imageKey.trim() === '' || error) {
    return (
      <div className={`${className} bg-gray-100 flex items-center justify-center`}>
        <PackageIcon className="w-12 h-12 text-gray-400" />
      </div>
    )
  }

  if (loading) {
    return (
      <div className={`${className} bg-gray-100 flex items-center justify-center`}>
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-400"></div>
      </div>
    )
  }

  if (signedUrl) {
    return <img src={signedUrl} alt={alt} className={className} key={signedUrl} />
  }

  // Fallback si no hay signedUrl
  return (
    <div className={`${className} bg-gray-100 flex items-center justify-center`}>
      <PackageIcon className="w-12 h-12 text-gray-400" />
    </div>
  )
}

export default ProductImage

